import { useState, useContext, useMemo, ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { useApi } from '../features/api/useApi';
import { useCampuses } from '../features/Campus/useCampusQueries';
import Loading from '../components/Loading';
import { useAuthToken } from '../hooks/useAuthToken';
import {
  CampusContext,
  CampusContextType,
  CampusFeatureFlags,
  CampusRecentEntities,
  ThermalZoneTactic,
  ControlMode,
  ThermalZoneSummary,
} from './CampusContext';
import { CONTROL_MODE } from '../config/control';
import { setThermalZoneStatus } from '../utilities/ProcessRealtimeZoneHelper';

import configuration from '../config.js';

const campusHasBattery = (batteryData: any): boolean => {
  if (!batteryData || batteryData.length === 0 || batteryData?.gridInterconnects?.length === 0) {
    return false;
  } else if (batteryData?.gridInterconnects?.length > 0) {
    return batteryData.gridInterconnects.some(
      (interconnect: any) => interconnect.acBatteries.length > 0
    );
  }
  return true;
};

const assignControlMode = (tactic: ThermalZoneTactic): ControlMode | null => {
  const now = new Date();
  const start = new Date(tactic.startDateTime);
  const end = new Date(tactic.endDateTime);

  // if the tactic blockCode is not a valid CONTROL_MODE, return null
  // There are some unexpected blockCodes for example 'NotSelected'
  if (!CONTROL_MODE[tactic.blockCode.toUpperCase()]) {
    return null;
  }

  if (now >= start && now <= end) {
    const untilDesc = () => {
      const untilString =
        end > now
          ? `Until ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
          : '';

      if (untilString.length > 0) {
        return { untilDesc: untilString };
      }
    };

    return {
      id: `${tactic.blockCode}-${tactic.startDateTime}`,
      mode: tactic.blockCode,
      isActive: true,
      duration: {
        startTime: tactic.startDateTime,
        endTime: tactic.endDateTime,
      },
      ...untilDesc(),
    };
  }

  return {
    id: `${tactic.blockCode}-${tactic.startDateTime}`,
    mode: tactic.blockCode,
    isActive: false,
    duration: {
      startTime: tactic.startDateTime,
      endTime: tactic.endDateTime,
    },
  };
};

// Hard ceiling on how long the app is held back waiting for the campus list.
const CAMPUS_LIST_GATE_MS = 3000;

interface CampusContextProviderProps {
  children: ReactNode;
}

export const CampusContextProvider = ({ children }: CampusContextProviderProps) => {
  const { fetchJson } = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const pathParts = location.pathname.split('/');
  const page = pathParts[1];
  const campusId = pathParts[2] || '';
  const [controlModes, setControlModes] = useState<ControlMode[]>([]);
  const [buildingId, setBuildingId] = useState('');
  const [gxId, setGxId] = useState('');
  const [zoneId, setZoneId] = useState('');

  // Fetched before anything else on the page: it carries notAnEms per campus and
  // gates every campus-scoped query below. Deliberately not gated on campusId so it
  // also runs on routes without one. Gated on auth because on the Auth0 callback
  // render there is no token yet, and a 401 here would hold every other query shut.
  // useAuthToken rather than authentication.isLoggedIn(): this provider is a parent
  // of App, which is what drives the handshake, so a plain render-time read would
  // only re-evaluate if something unrelated happened to re-render us.
  const { hasToken: isAuthenticated } = useAuthToken();

  const {
    data: campusList,
    isSuccess: campusListReady,
    isError: campusListFailed,
  } = useCampuses({ enabled: isAuthenticated });

  // Latches once and never resets, so the app can never re-block after painting.
  const [gateExpired, setGateExpired] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setGateExpired(true), CAMPUS_LIST_GATE_MS);
    return () => clearTimeout(timer);
  }, []);

  // Folding the deadline in here is what makes "continue if it takes longer" mean
  // continue: without it a timed-out release would render an app that never loads
  // any campus data. campusListFailed releases early rather than burning the full
  // wait once React Query has exhausted its retries.
  // gateExpired latches on a timer, so campusGateOpen alone says nothing about auth:
  // 3s after mount, or any time after, it is true whether or not a token exists.
  // Every query below hits an authenticated endpoint via the legacy fetchJson, so
  // they gate on the token too -- otherwise they fire bare on logout.
  const campusGateOpen = campusListReady || campusListFailed || gateExpired;

  const {
    data: batteryData,
    // isLoading: batteryLoading,
    // error: batteryError,
  } = useQuery({
    queryKey: ['devices', campusId, 'battery'],
    queryFn: () => fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/acBatteries`),
    enabled: isAuthenticated && !!campusId && campusGateOpen,
  });

  const {
    data: campusComfortData,
    isLoading: campusComfortLoading,
    error: campusComfortError,
  } = useQuery({
    queryKey: ['campus', campusId, 'comfort'],
    queryFn: () => fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/campus/comfort`),
    enabled: isAuthenticated && !!campusId && campusGateOpen,
    refetchInterval: 5000,
  });

  const { data: campusEnergyData } = useQuery({
    queryKey: ['campus', campusId, 'energy'],
    queryFn: () => fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/campus/energy`),
    enabled: isAuthenticated && !!campusId && campusGateOpen,
  });

  const { data: campusEvData } = useQuery({
    queryKey: ['devices', campusId, 'ev'],
    queryFn: () =>
      fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/campus/evChargers`),
    enabled: isAuthenticated && !!campusId && campusGateOpen,
  });

  const { data: solarData } = useQuery({
    queryKey: ['campus', 'solar'],
    queryFn: () => {
      return fetchJson(`${configuration['backend_host']}/ahiapi/trendsSolarCampuses`);
    },
    // refetchInterval: 5000,
    retryDelay: 5000,
    enabled: isAuthenticated && !!campusId && campusGateOpen,
  });

  const campusInterconnects = useMemo(
    () =>
      campusComfortData?.campusDescription?.gridInterconnects?.map(
        (interconnect: any) => interconnect?.gridInterconnectEntityKey
      ) ?? [],
    [campusComfortData]
  );

  const { data: interconnectData } = useQuery({
    queryKey: ['interconnect', campusInterconnects[0]],
    queryFn: () => {
      if (campusInterconnects.length === 0) return;
      return fetchJson(
        `${configuration['backend_host']}/admin/monitor/${campusInterconnects[0]}/gridInterconnect/graphData/live`
      );
    },
    // refetchInterval: 5000,
    retryDelay: 5000,
    enabled: isAuthenticated && campusInterconnects.length > 0 && !!campusId && campusGateOpen,
  });

  const solarGx = useMemo(
    () => solarData?.filter((campus: any) => campus.campusEntityKeyToken === campusId) ?? [],
    [solarData, campusId]
  );

  const campusEvChargers = useMemo(
    () => campusEvData?.gridInterconnectEvChargers ?? [],
    [campusEvData]
  );

  const hasSingleGx = campusInterconnects.length === 1;
  const hasSolar = solarGx.some((campus: any) => campus.energySite !== null);
  const hasEv = campusEvChargers.some((campusEvCharger: any) =>
    campusEvCharger.evChargerGroups?.some((group: any) => group.evChargers?.length > 0)
  );

  useEffect(() => {
    setBuildingId('');
    setGxId('');
    setZoneId('');
  }, [campusId]);

  useEffect(() => {
    if (page === 'comfort') {
      if (pathParts[3]) setBuildingId(pathParts[3]);
      if (pathParts[4]) setZoneId(pathParts[4]);
    } else if (page && ['energy', 'trends', 'solar'].includes(page)) {
      if (pathParts[3]) setGxId(pathParts[3]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!interconnectData) return;
    getThermalZoneTactics();
  }, [interconnectData]);

  const campusName = campusComfortData?.campusDescription?.longCampusDisplayName || 'Campus';

  const getThermalZoneTactics: () => void = () => {
    const { hasThermalZones } = featureFlags;
    const thermalZoneTactics = interconnectData?.blockCategories?.find(
      (category: any) => category.categoryName === 'ThermalZoneTactic'
    )?.blocks;
    const newThermalZoneTactics: ControlMode[] = [];

    for (const tactic of thermalZoneTactics || []) {
      const controlMode = assignControlMode(tactic);
      if (controlMode) {
        newThermalZoneTactics.push(controlMode);
      }
    }

    setControlModes(hasThermalZones ? newThermalZoneTactics : []);
  };

  const getZoneActiveControlStrategy = (zoneData: any): ControlMode | null => {
    if (zoneData?.offline) return { id: 'offline', mode: 'OFFLINE', isActive: true };
    if (zoneData?.unmanaged) return { id: 'unmanaged', mode: 'UNMANAGED', isActive: true };
    if (zoneData?.inLocalOverride)
      return {
        id: `local-override-${zoneData.thermalZoneEntityKey}`,
        mode: 'LOCAL_OVERRIDE',
        isActive: true,
      };
    if (zoneData?.inOverride)
      return {
        id: `remote-override-${zoneData.thermalZoneEntityKey}`,
        mode: 'REMOTE_OVERRIDE',
        isActive: true,
      };

    if (zoneData?.inEvent && zoneData?.eventName)
      return {
        id: `event-${zoneData.thermalZoneEntityKey}`,
        mode: 'EVENT',
        isActive: true,
      };

    const activeTactic = controlModes.find((mode) => mode.isActive);
    if (!activeTactic && !zoneData?.unmanaged) {
      return { id: 'standard', mode: 'STANDARD', isActive: true };
    }

    return activeTactic ?? null;
  };

  const currentCampus = useMemo(
    () => campusList?.find((campus: any) => campus.campusEntityKey === campusId),
    [campusList, campusId]
  );

  const notAnEms = useMemo(() => {
    if (currentCampus?.notAnEms !== undefined) return currentCampus.notAnEms;
    // Fall back to the comfort payload for a campusId absent from the list.
    return campusComfortData?.campusDescription?.notAnEms ?? false;
  }, [currentCampus, campusComfortData]);

  // Root carries no campusId, so the active campus is the one the app is about to
  // select: campuses[0]. That default is set by the reducer (store.jsx:26-28) and by
  // processCampusJSON (App.jsx:432), and nothing persists a previous selection.
  const isRootPage = location.pathname === '/';
  const rootCampus = isRootPage ? campusList?.[0] : undefined;

  // campusListReady, not campusGateOpen: only redirect when the flag was genuinely
  // set. A timed-out or failed list leaves routing exactly as it is today.
  const rootRedirectTarget =
    isRootPage && campusListReady && rootCampus?.notAnEms === true && rootCampus?.campusEntityKey
      ? `/trends/${rootCampus.campusEntityKey}`
      : null;

  useEffect(() => {
    if (rootRedirectTarget) {
      navigate(rootRedirectTarget, { replace: true });
    }
  }, [rootRedirectTarget, navigate]);

  const thermalZones: ThermalZoneSummary[] =
    campusComfortData?.buildings?.flatMap((building: any) =>
      (building.thermalZones ?? []).map(
        (zone: any): ThermalZoneSummary => ({
          thermalZoneEntityKey: zone.thermalZoneEntityKey,
          name: zone.longThermalZoneDisplayName,
          buildingEntityKey: building.buildingDescription.buildingEntityKey,
          buildingName: building.buildingDescription.longBuildingDisplayName,
          status: setThermalZoneStatus(zone),
          activeControlStrategy: getZoneActiveControlStrategy(zone),
        })
      )
    ) ?? [];

  const featureFlags: CampusFeatureFlags = {
    hasBattery: batteryData ? campusHasBattery(batteryData) : false,
    hasSolar: hasSolar,
    hasEv: hasEv,
    hasSingleGX: hasSingleGx,
    hasThermalZones: thermalZones.length > 0,
    notAnEms: notAnEms,
  };

  const contextValue: CampusContextType = {
    meta: {
      campusName: campusName || '',
      campusId: campusId || '',
      timezone: interconnectData?.timeZoneCode || '',
    },
    featureFlags,
    controlModes: controlModes,
    thermalZones,
    campusData: {
      data: campusComfortData,
      isLoading: campusComfortLoading,
      error: campusComfortError,
    },
    recentEntities: {
      campusId,
      buildingId,
      gxId,
      zoneId,
    },
  };

  // App owns the Auth0 redirect (App.jsx -> handleAuthentication), so blocking an
  // unauthenticated visitor would stop them ever reaching the login page. lobbyKiosk
  // renders through App's unauthenticated branch and does not consume notAnEms.
  const isKioskRoute = page?.toLowerCase() === 'lobbykiosk';
  const shouldBlock = isAuthenticated && !isKioskRoute && (!campusGateOpen || !!rootRedirectTarget);

  if (shouldBlock) {
    return (
      <CampusContext.Provider value={contextValue}>
        <Loading message="Loading..." />
      </CampusContext.Provider>
    );
  }

  return <CampusContext.Provider value={contextValue}>{children}</CampusContext.Provider>;
};

export const useCampusContext = (): CampusContextType => {
  const context = useContext(CampusContext);
  if (context === undefined) {
    throw new Error('useCampusContext must be used within a CampusContextProvider');
  }
  return context;
};

// hooks
export const useCampusFeatureFlags = (): CampusFeatureFlags => useCampusContext().featureFlags;
export const useCampusMeta = (): CampusContextType['meta'] => useCampusContext().meta;
export const useCampusData = (): CampusContextType['campusData'] => useCampusContext().campusData;
export const useCampusControlModes = (): ControlMode[] | undefined =>
  useCampusContext().controlModes;
export const useCampusThermalZones = (): ThermalZoneSummary[] | undefined =>
  useCampusContext().thermalZones;
export const useCampusWeather = (): any => {
  const campusData = useCampusContext().campusData?.data?.weather;
  return campusData || null;
};
export const useRecentEntities = (): CampusRecentEntities => useCampusContext().recentEntities;
